import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  BadgeCheck,
  CalendarDays,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  ShieldCheck,
  Tag,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const RegisterSection = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const isStandalone = location.pathname === '/register';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    time_slot: '',
  });

  const timeSlots = [
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '02:00 PM',
    '03:00 PM',
    '04:00 PM',
  ];
  const bookedSlots = ['10:00 AM', '02:00 PM'];

  const validateForm = () => {
    let newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!/^[A-Za-z\s]{2,}$/.test(formData.name)) {
      newErrors.name = 'Enter a valid name (letters only)';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10,13}$/.test(formData.phone)) {
      newErrors.phone = 'Enter valid 10 to 13-digit phone number';
    }

    if (!formData.preferred_date) {
      newErrors.preferred_date = 'Please select a date';
    }

    if (!formData.time_slot) {
      newErrors.time_slot = 'Please select a time slot';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 13) return;
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    // Clear error on change
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSlotSelect = (slot) => {
    if (!bookedSlots.includes(slot)) {
      setFormData((prev) => ({ ...prev, time_slot: slot }));
      if (errors.time_slot) setErrors((prev) => ({ ...prev, time_slot: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill out all required fields correctly.');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`https://bigbariatrics.com/api/send-mail`, formData);
      toast.success(t('form_success'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferred_date: '',
        time_slot: '',
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <UserCheck className="w-5 h-5" />,
      text: 'Personal discussion with our Obesity Coordinator',
    },
    {
      icon: <Tag className="w-5 h-5" />,
      text: '10% discount on pathology tests & medications',
    },
    {
      icon: <Mail className="w-5 h-5" />,
      text: 'Confirmation sent to your email & WhatsApp',
    },
    {
      icon: <Phone className="w-5 h-5" />,
      text: 'SMS notification sent to your registered mobile',
    },
  ];

  return (
    <section
      id="register"
      className={`min-h-screen bg-gradient-to-br from-[#fdf6f0] via-white to-[#fff1f1] ${isStandalone ? 'pt-24 pb-16' : 'py-20'
        }`}
      data-testid="register-section"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Google Font */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');`}</style>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-[#800000]/10 text-[#800000] text-xs font-bold uppercase tracking-[0.18em] px-4 py-1.5 rounded-full mb-4">
            GastroCare Bhopal
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-3">
            {t('register_title')}
          </h1>
          <p className="text-slate-500 text-lg max-w-xl mx-auto">
            Book your consultation slot and take the first step towards a
            healthier you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* ── LEFT: Info Panel ──────────────────────── */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Registration Fee Card */}
            <div className="relative overflow-hidden rounded-2xl bg-[#800000] text-white p-7 shadow-xl">
              {/* decorative blob */}
              <div
                className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20"
                style={{ background: 'radial-gradient(circle, #fff 0%, transparent 70%)' }}
              />
              <div className="relative z-10">
                <ShieldCheck className="w-9 h-9 mb-4 opacity-90" />
                <h3 className="text-xl font-bold mb-1">Registration Fee</h3>
                <p className="text-4xl font-extrabold tracking-tight mb-1">
                  ₹200
                </p>
                <p className="text-sm font-medium text-red-200">
                  Non-Refundable
                </p>
              </div>
            </div>

            {/* Benefits Card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-bold text-slate-800 text-base mb-4 flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-[#800000]" />
                What You Get
              </h3>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 flex-shrink-0 text-[#800000]">
                      {b.icon}
                    </span>
                    <span className="text-slate-600 text-sm leading-snug">
                      {b.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>



          </div>

          {/* ── RIGHT: Form ───────────────────────────── */}
          <div
            className="lg:col-span-3 bg-white rounded-3xl shadow-xl border border-slate-100 p-8"
            data-testid="register-form-wrapper"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              Book Your Slot
            </h2>
            <p className="text-slate-500 text-sm mb-7">
              Fill in the details below to secure your appointment.
            </p>

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="appointment-form"
            >
              {/* Name */}
              <div>
                <Label
                  htmlFor="name"
                  className="text-slate-700 font-semibold text-sm"
                >
                  {t('form_name')}
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`mt-1.5 h-11 rounded-xl border-slate-200 focus:border-[#800000] focus:ring-[#800000]/20 ${errors.name ? 'border-red-400' : ''
                    }`}
                  placeholder="Enter your full name"
                  data-testid="form-name-input"
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Email & Phone – side by side on md+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="email"
                    className="text-slate-700 font-semibold text-sm"
                  >
                    {t('form_email')}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`mt-1.5 h-11 rounded-xl border-slate-200 focus:border-[#800000] focus:ring-[#800000]/20 ${errors.email ? 'border-red-400' : ''
                      }`}
                    placeholder="you@email.com"
                    data-testid="form-email-input"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="phone"
                    className="text-slate-700 font-semibold text-sm"
                  >
                    {t('form_phone')}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className={`mt-1.5 h-11 rounded-xl border-slate-200 focus:border-[#800000] focus:ring-[#800000]/20 ${errors.phone ? 'border-red-400' : ''
                      }`}
                    placeholder="10-digit mobile number"
                    data-testid="form-phone-input"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>

              {/* Preferred Date */}
              <div>
                <Label
                  htmlFor="preferred_date"
                  className="text-slate-700 font-semibold text-sm flex items-center gap-1.5"
                >
                  <CalendarDays className="w-4 h-4 text-[#800000]" />
                  {t('form_date')}
                </Label>
                <Input
                  id="preferred_date"
                  name="preferred_date"
                  type="date"
                  value={formData.preferred_date}
                  onChange={handleChange}
                  className={`mt-1.5 h-11 rounded-xl border-slate-200 focus:border-[#800000] focus:ring-[#800000]/20 ${errors.preferred_date ? 'border-red-400' : ''
                    }`}
                  data-testid="form-date-input"
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.preferred_date && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.preferred_date}
                  </p>
                )}
              </div>

              {/* Time Slots — always visible */}
              <div>
                <Label className="text-slate-700 font-semibold text-sm flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-[#800000]" />
                  {t('form_slot')}
                </Label>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-2 mb-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-full bg-emerald-500" />
                    Available
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-full bg-slate-300" />
                    Booked
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-full bg-[#800000]" />
                    Selected
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2.5">
                  {timeSlots.map((slot) => {
                    const isBooked = bookedSlots.includes(slot);
                    const isSelected = formData.time_slot === slot;
                    return (
                      <button
                        key={slot}
                        type="button"
                        disabled={isBooked}
                        onClick={() => handleSlotSelect(slot)}
                        className={`relative py-3 px-2 rounded-xl text-sm font-semibold transition-all duration-200 border-2 focus:outline-none
                          ${isBooked
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-70'
                            : isSelected
                              ? 'bg-[#800000] text-white border-[#800000] shadow-lg scale-[1.04]'
                              : 'bg-white text-slate-700 border-emerald-200 hover:border-[#800000] hover:text-[#800000] hover:shadow-md'
                          }`}
                      >
                        <span className="block">{slot}</span>
                        <span
                          className={`block text-[9px] uppercase font-bold mt-0.5 tracking-wide ${isBooked
                            ? 'text-slate-400'
                            : isSelected
                              ? 'text-red-200'
                              : 'text-emerald-600'
                            }`}
                        >
                          {isBooked ? 'Booked' : isSelected ? '✓ Selected' : 'Available'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {errors.time_slot && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.time_slot}
                  </p>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 rounded-xl text-base font-bold flex items-center justify-center gap-2 transition-all duration-200"
                style={{
                  background: isSubmitting
                    ? '#b91c1c'
                    : 'linear-gradient(135deg, #800000 0%, #b91c1c 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 20px rgba(128,0,0,0.35)',
                }}
                data-testid="form-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Registering...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    <span>Confirm Registration</span>
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegisterSection;
